import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import constants from '../../constants';

type Status = 'idle' | 'generating' | 'ready' | 'failed';

type FileMeta = {
	has_html: boolean;
	has_pdf: boolean;
	html_name?: string | null;
	pdf_name?: string | null;
	uploaded_at?: string | null;
} | null;

type StatusPayload = {
	executiveSummaryFile?: FileMeta;
	executiveSummary: string;
	executiveSummaryDraft: string;
	executiveSummaryStatus: Status;
	executiveSummaryGeneratedAt: string | null;
	executiveSummaryError: string;
	executiveSummaryStructured: any;
	executiveSummaryStructuredDraft: any;
};

const POLL_INTERVAL_MS = 10_000;

const statusLabel = (s: Status): string =>
	({
		idle: 'מוכן',
		generating: 'בתהליך',
		ready: 'טיוטה ממתינה לאישור',
		failed: 'נכשל',
	}[s] || s);

const statusColor = (s: Status): 'default' | 'info' | 'success' | 'error' =>
	({
		idle: 'default' as const,
		generating: 'info' as const,
		ready: 'success' as const,
		failed: 'error' as const,
	}[s]);

const ExecutiveSummaryGenerator = ({ companyId }: { companyId: string }) => {
	const notify = useNotify();
	const refresh = useRefresh();
	const [status, setStatus] = useState<Status>('idle');
	const [currentSummary, setCurrentSummary] = useState<string>('');
	const [draft, setDraft] = useState<string>('');
	const [structuredDraft, setStructuredDraft] = useState<any>(null);
	const [generatedAt, setGeneratedAt] = useState<string | null>(null);
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(true);
	const [actionLoading, setActionLoading] = useState<boolean>(false);
	const draftDirty = useRef<boolean>(false);
	// GH-682: uploaded report (HTML + optional PDF)
	const [file, setFile] = useState<FileMeta>(null);
	const [fileBusy, setFileBusy] = useState<boolean>(false);

	const fetchStatus = async () => {
		try {
			const res = await axios.get<StatusPayload>(
				`${constants.API_URL}/company/${companyId}/executive-summary/status`,
			);
			const data = res.data;
			setStatus(data.executiveSummaryStatus);
			setCurrentSummary(data.executiveSummary || '');
			setGeneratedAt(data.executiveSummaryGeneratedAt);
			setError(data.executiveSummaryError || '');
			// Only update the draft from server if the user hasn't started editing
			if (!draftDirty.current) {
				setDraft(data.executiveSummaryDraft || '');
			}
			setStructuredDraft(data.executiveSummaryStructuredDraft || null);
			setFile(data.executiveSummaryFile || null);
		} catch (e: any) {
			console.error(e);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchStatus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId]);

	useEffect(() => {
		if (status !== 'generating') return;
		const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, companyId]);

	const handleGenerate = async () => {
		if (status === 'generating') return;
		const ok = window.confirm(
			'הפעולה תיקח מספר דקות ותעדכן רק את הטיוטה (לא את התקציר שמוצג ללקוח). להמשיך?',
		);
		if (!ok) return;
		try {
			setActionLoading(true);
			await axios.post(`${constants.API_URL}/company/${companyId}/executive-summary/generate`, {});
			notify('ייצור התקציר התחיל. ניתן לעזוב את העמוד — סטטוס יתעדכן אוטומטית.', { type: 'info' });
			draftDirty.current = false;
			await fetchStatus();
		} catch (e: any) {
			notify(e?.response?.data?.message || 'שגיאה בהפעלת הייצור', { type: 'error' });
		} finally {
			setActionLoading(false);
		}
	};

	const handleApprove = async () => {
		const trimmed = (draft || '').trim();
		if (!trimmed) {
			notify('הטיוטה ריקה — אין מה לאשר', { type: 'warning' });
			return;
		}
		const ok = window.confirm(
			'הטיוטה תועבר לתקציר המוצג ללקוח ותחליף את התוכן הקיים. להמשיך?',
		);
		if (!ok) return;
		try {
			setActionLoading(true);
			await axios.post(`${constants.API_URL}/company/${companyId}/executive-summary/approve`, {
				summary: trimmed,
				structured: structuredDraft,
			});
			notify('הטיוטה אושרה ועודכנה בתקציר הראשי', { type: 'success' });
			draftDirty.current = false;
			await fetchStatus();
			refresh();
		} catch (e: any) {
			notify(e?.response?.data?.message || 'שגיאה באישור הטיוטה', { type: 'error' });
		} finally {
			setActionLoading(false);
		}
	};

	// ---- GH-682: report file upload (base64 data URI, like the other document uploads) ----
	const readAsDataUrl = (f: File) =>
		new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(String(reader.result));
			reader.onerror = reject;
			reader.readAsDataURL(f);
		});

	const handleFileUpload = async (kind: 'html' | 'pdf', f: File | undefined) => {
		if (!f) return;
		try {
			setFileBusy(true);
			const dataUri = await readAsDataUrl(f);
			const body = kind === 'html' ? { html: dataUri, html_name: f.name } : { pdf: dataUri, pdf_name: f.name };
			const res = await axios.post(`${constants.API_URL}/company/${companyId}/executive-summary/file`, body);
			setFile(res.data?.executiveSummaryFile || null);
			notify(kind === 'html' ? 'קובץ ה-HTML הועלה — מוצג ל-HR מעכשיו' : 'קובץ ה-PDF הועלה', { type: 'success' });
		} catch (e: any) {
			notify(e?.response?.data?.message || 'ההעלאה נכשלה', { type: 'error' });
		} finally {
			setFileBusy(false);
		}
	};

	const handleFileRemove = async (which: 'html' | 'pdf') => {
		if (!window.confirm(which === 'html' ? 'להסיר את דוח ה-HTML? ה-HR יחזור לראות את התקציר הרגיל.' : 'להסיר את קובץ ה-PDF?')) return;
		try {
			setFileBusy(true);
			const res = await axios.delete(`${constants.API_URL}/company/${companyId}/executive-summary/file`, {
				params: { which },
			});
			setFile(res.data?.executiveSummaryFile || null);
			notify('הוסר', { type: 'success' });
		} catch (e: any) {
			notify(e?.response?.data?.message || 'ההסרה נכשלה', { type: 'error' });
		} finally {
			setFileBusy(false);
		}
	};

	// Authenticated preview: fetch as blob and open an object URL (the S3 objects are private).
	const openFile = async (which: 'html' | 'pdf') => {
		try {
			const res = await axios.get(`${constants.API_URL}/company/${companyId}/executive-summary/file/${which}`, {
				responseType: 'blob',
			});
			const url = URL.createObjectURL(res.data);
			window.open(url, '_blank', 'noopener');
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		} catch (e: any) {
			notify('לא ניתן לפתוח את הקובץ', { type: 'error' });
		}
	};

	if (loading) {
		return (
			<Box sx={{ p: 2 }}>
				<CircularProgress size={20} />
			</Box>
		);
	}

	const fileRow = (kind: 'html' | 'pdf') => {
		const has = kind === 'html' ? file?.has_html : file?.has_pdf;
		const name = kind === 'html' ? file?.html_name : file?.pdf_name;
		const accept = kind === 'html' ? '.html,.htm,text/html' : '.pdf,application/pdf';
		return (
			<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
				<Typography variant="body2" sx={{ minWidth: 60, fontWeight: 600 }}>
					{kind === 'html' ? 'HTML' : 'PDF'}
				</Typography>
				{has ? (
					<>
						<Chip label={name || (kind === 'html' ? 'דוח' : 'קובץ')} size="small" color={kind === 'html' ? 'success' : 'default'} />
						<Button size="small" variant="outlined" onClick={() => openFile(kind)} disabled={fileBusy}>
							פתח
						</Button>
						<Button size="small" variant="outlined" color="error" onClick={() => handleFileRemove(kind)} disabled={fileBusy}>
							הסר
						</Button>
					</>
				) : (
					<Typography variant="caption" color="text.secondary">
						{kind === 'html' ? 'אין דוח — ה-HR רואה את התקציר הרגיל' : 'לא הועלה (אופציונלי — כפתור "הורדת PDF" ל-HR)'}
					</Typography>
				)}
				<Button size="small" variant="contained" component="label" disabled={fileBusy}>
					{has ? 'החלף' : 'העלאה'}
					<input
						type="file"
						hidden
						accept={accept}
						onChange={(e) => {
							handleFileUpload(kind, e.target.files?.[0]);
							e.target.value = '';
						}}
					/>
				</Button>
			</Stack>
		);
	};

	return (
		<Box dir="rtl" sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, mb: 2, backgroundColor: '#fafafa' }}>
			{/* GH-682: uploaded report — when an HTML exists it REPLACES the generated summary on the HR page */}
			<Box sx={{ p: 2, mb: 3, border: '1px solid #d7dce5', borderRadius: 2, backgroundColor: '#fff' }}>
				<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
					<Typography variant="h6">דוח תקציר מנהלים — קובץ</Typography>
					{file?.has_html && <Chip label="מוצג ל-HR" color="success" size="small" />}
					{file?.uploaded_at && (
						<Typography variant="caption" color="text.secondary">
							הועלה {moment(file.uploaded_at).format('D.M.YYYY HH:mm')}
						</Typography>
					)}
					{fileBusy && <CircularProgress size={16} />}
				</Stack>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
					קובץ HTML סטטי (בלי JavaScript) מוצג ל-HR בטאב "תקציר מנהלים" במקום התקציר שנוצר ב-AI. PDF אופציונלי להורדה. הקבצים פרטיים.
				</Typography>
				{fileRow('html')}
				{fileRow('pdf')}
			</Box>

			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
				<Typography variant="h6">תקציר מנהלים — ייצור AI</Typography>
				<Chip label={statusLabel(status)} color={statusColor(status)} size="small" />
				{generatedAt && (
					<Typography variant="caption" color="text.secondary">
						נוצר {moment(generatedAt).fromNow()}
					</Typography>
				)}
			</Stack>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
				<Button
					variant="contained"
					color="primary"
					onClick={handleGenerate}
					disabled={actionLoading || status === 'generating'}
					startIcon={status === 'generating' ? <CircularProgress size={16} /> : null}
				>
					{status === 'generating' ? 'מייצר...' : 'ייצור תקציר חדש'}
				</Button>
				<Button
					variant="contained"
					color="success"
					onClick={handleApprove}
					disabled={actionLoading || !draft.trim() || status === 'generating'}
				>
					אישור והעברה לתקציר הראשי
				</Button>
			</Stack>

			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				טיוטה (ניתנת לעריכה לפני אישור)
			</Typography>
			<TextField
				multiline
				minRows={10}
				maxRows={30}
				fullWidth
				value={draft}
				onChange={(e) => {
					draftDirty.current = true;
					setDraft(e.target.value);
				}}
				placeholder={
					status === 'generating'
						? 'מייצר... ייקח כמה דקות. ניתן לעזוב את העמוד — נבדוק כל 10 שניות.'
						: 'אין טיוטה כרגע — לחץ "ייצור תקציר חדש"'
				}
				disabled={status === 'generating'}
				sx={{ backgroundColor: '#fff' }}
			/>

			{structuredDraft && (
				<Box sx={{ mt: 3 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						JSON מובנה (לתצוגת גרפים באתר — אישור הטיוטה ישמור גם אותו)
					</Typography>
					<Box
						component="pre"
						dir="ltr"
						sx={{
							backgroundColor: '#1e1e1e',
							color: '#d4d4d4',
							p: 2,
							borderRadius: 1,
							fontSize: 12,
							maxHeight: 400,
							overflow: 'auto',
							margin: 0,
						}}
					>
						{JSON.stringify(structuredDraft, null, 2)}
					</Box>
				</Box>
			)}

			{currentSummary && (
				<Box sx={{ mt: 2 }}>
					<Typography variant="caption" color="text.secondary">
						התקציר הנוכחי ({currentSummary.length} תווים) מוצג ללקוח. אישור הטיוטה ידרוס אותו.
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default ExecutiveSummaryGenerator;
